import { useEffect, useState } from "react";
import StepQuestions, { type Question, type Answer } from "./StepQuestions";
import StepSuccess from "./StepSuccess";
import ProfileIncompleteGate from "./ProfileIncompleteGate";
import type { JobQuestion } from "@applicant-reviewer/api";
import {
  useCreateApplication,
  useSaveAnswerStep,
  useSubmitApplication,
} from "../../../../hooks/mutations/useApplicationMutations";
import { useProfile } from "../../../../hooks/queries/useProfile";

// These types are kept for backward compatibility with StepIdentity and StepContact
// which are no longer rendered by the wizard but are retained as files.
export interface IdentityData {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

export interface ContactData {
  email: string;
  phone: string;
  isPhoneVerified: boolean;
}

interface ApplicationWizardProps {
  jobId?: string;
  questions: JobQuestion[];
  onComplete: (data: { answers: Answer[] }) => void;
  onCancel: () => void;
}

// Map backend JobQuestion to StepQuestions Question format
function mapJobQuestions(backendQuestions: JobQuestion[]): Question[] {
  return backendQuestions.map((q, index) => {
    let type: Question["type"];

    if (q.type === "multiple_choice") {
      type = "checkbox";
    } else if (q.type === "boolean") {
      type = "checkbox";
    } else if (q.type === "experience") {
      type = "text";
    } else {
      type = q.type as Question["type"];
    }

    return {
      id: String(index),
      type,
      prompt: q.prompt,
      options: q.options,
      multiSelect: q.type === "experience" ? false : undefined,
      maxDuration: q.maxDuration,
    };
  });
}

export default function ApplicationWizard({ jobId, questions, onComplete, onCancel }: ApplicationWizardProps) {
  const [currentStep, setCurrentStep] = useState<0 | 1>(0);
  const [questionProgress, setQuestionProgress] = useState(0);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const { data: profile, isLoading: isProfileLoading } = useProfile();

  const createApplication = useCreateApplication();
  const saveAnswer = useSaveAnswerStep();
  const submitApplication = useSubmitApplication();

  // Map backend questions to StepQuestions format
  const mappedQuestions = mapJobQuestions(questions);

  // Eagerly create the application draft once profile is confirmed complete
  useEffect(() => {
    if (profile?.profileComplete && jobId && !applicationId && !createApplication.isPending && !createApplication.isSuccess) {
      createApplication.mutate(
        { jobPostingId: jobId },
        {
          onSuccess: (result) => {
            setApplicationId(result.applicationId);
          },
          onError: (err) => {
            console.error("Failed to create application:", err);
            setApiError("Failed to start application. Please try again.");
          },
        }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.profileComplete, jobId]);

  // Show loading state while profile is being fetched
  if (isProfileLoading) {
    return (
      <div className="fixed inset-0 z-[200] bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Gate: if profile is not complete, show the ProfileIncompleteGate
  if (!profile?.profileComplete) {
    return <ProfileIncompleteGate profile={profile ?? { candidateId: "", email: "", profileComplete: false }} />;
  }

  const handleQuestionsComplete = async (questionAnswers: Answer[]) => {
    setAnswers(questionAnswers);
    setApiError(null);

    // If no application yet (e.g. no jobId or creation failed), fall through gracefully
    let resolvedApplicationId = applicationId;

    if (!resolvedApplicationId && jobId) {
      try {
        const result = await createApplication.mutateAsync({ jobPostingId: jobId });
        resolvedApplicationId = result.applicationId;
        setApplicationId(resolvedApplicationId);
      } catch (err) {
        console.error("Failed to create application:", err);
        setApiError("Failed to start application. Please try again.");
        return;
      }
    }

    if (resolvedApplicationId) {
      // Save all text/choice answers; skip blobs (video handled in VideoRecorder)
      const savePromises = questionAnswers
        .filter((a) => !(a.value instanceof Blob))
        .map((a) =>
          saveAnswer.mutateAsync({
            applicationId: resolvedApplicationId!,
            questionId: a.questionId,
            payload: {
              answer: Array.isArray(a.value) ? a.value.join(", ") : (a.value as string),
            },
          })
        );

      try {
        await Promise.all(savePromises);
      } catch (err) {
        console.error("Failed to save some answers:", err);
        // Non-fatal: proceed to submit
      }

      submitApplication.mutate(resolvedApplicationId, {
        onSuccess: () => setCurrentStep(1),
        onError: (err) => {
          console.error("Failed to submit application:", err);
          setApiError("Failed to submit application. Please try again.");
        },
      });
    } else {
      setCurrentStep(1);
    }
  };

  const handleSuccessDone = () => {
    onComplete({ answers });
  };

  const questionCount = mappedQuestions.length;
  const totalSteps = questionCount;

  const currentStepNumber = (() => {
    if (currentStep === 0) {
      const clampedProgress = Math.min(Math.max(questionProgress, 0), Math.max(questionCount - 1, 0));
      return clampedProgress + 1;
    }
    return totalSteps;
  })();

  const isProcessing =
    createApplication.isPending ||
    submitApplication.isPending;

  return (
    <div className="fixed inset-0 z-[200] bg-white flex flex-col">
      {/* Progress Header (Hide on success screen) */}
      {currentStep !== 1 && (
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 shadow-md">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-white text-xl font-bold">Application Progress</h1>
              <button
                onClick={onCancel}
                className="text-white/80 hover:text-white text-sm font-medium"
              >
                Cancel
              </button>
            </div>

            {/* Progress Bar */}
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-white/20 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-white h-full transition-all duration-500 ease-out"
                  style={{ width: totalSteps > 0 ? `${(currentStepNumber / totalSteps) * 100}%` : "0%" }}
                />
              </div>
              <span className="text-white text-sm font-medium whitespace-nowrap">
                Step {currentStepNumber} of {totalSteps}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* API Error Banner */}
      {apiError && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3">
          <p className="text-red-700 text-sm">{apiError}</p>
        </div>
      )}

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="absolute inset-0 bg-white/70 z-[300] flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto">
        {currentStep === 0 && (
          <StepQuestions
            questions={mappedQuestions}
            onComplete={handleQuestionsComplete}
            onBack={onCancel}
            onQuestionProgress={setQuestionProgress}
          />
        )}
        {currentStep === 1 && (
          <StepSuccess
            phoneNumber={profile.phone ?? ""}
            onDone={handleSuccessDone}
          />
        )}
      </div>
    </div>
  );
}
